/**
 * ステータス更新モーダルコンポーネント
 *
 * UNIFIED_DEVELOPMENT_GUIDE準拠
 * - Mantine UI
 * - Zod Validation
 */

import { Modal, Stack, Select, Textarea, Group, Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { zodResolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';
import { IconCheck, IconX } from '@tabler/icons-react';
import type { PreRegistrationStatus, CustomerPreRegistration } from '../types';
import { PRE_REGISTRATION_STATUS_LABELS } from '../types';

// ========== Validation Schema ==========

const statusUpdateSchema = z.object({
  status: z.string().min(1, 'ステータスを選択してください'),
  notes: z.string().max(1000, 'メモは1000文字以内で入力してください').optional(),
});

type StatusUpdateFormValues = z.infer<typeof statusUpdateSchema>;

// ========== Component Props ==========

interface StatusUpdateModalProps {
  opened: boolean;
  onClose: () => void;
  registration: CustomerPreRegistration;
  onSubmit: (data: { status: string; notes?: string }) => void;
  isLoading?: boolean;
}

// ========== Component ==========

export const StatusUpdateModal = ({
  opened,
  onClose,
  registration,
  onSubmit,
  isLoading = false,
}: StatusUpdateModalProps) => {
  // ========== Form Setup ==========

  const form = useForm({
    validate: zodResolver(statusUpdateSchema),
    initialValues: {
      status: registration.status,
      notes: '',
    },
  });

  // ========== Handlers ==========

  const handleSubmit = (values: StatusUpdateFormValues) => {
    onSubmit({
      status: values.status,
      notes: values.notes || undefined,
    });
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // ========== Available Status Options ==========

  // 現在のステータスに応じて選択可能なステータスを制限
  const getAvailableStatuses = (currentStatus: PreRegistrationStatus) => {
    const transitions: Record<PreRegistrationStatus, PreRegistrationStatus[]> = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['cancelled'],
      converted: [], // 変換済みは変更不可
      cancelled: ['pending'], // キャンセルから未確認に戻すことは可能
    };

    return transitions[currentStatus] || [];
  };

  const availableStatuses = getAvailableStatuses(registration.status);

  const statusOptions = [
    { value: registration.status, label: `${PRE_REGISTRATION_STATUS_LABELS[registration.status]}（現在）`, disabled: true },
    ...availableStatuses.map(status => ({
      value: status,
      label: PRE_REGISTRATION_STATUS_LABELS[status],
    })),
  ];

  // ========== Render ==========

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="ステータス変更"
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* ステータス選択 */}
          <Select
            label="新しいステータス"
            placeholder="ステータスを選択"
            data={statusOptions}
            required
            {...form.getInputProps('status')}
            disabled={availableStatuses.length === 0}
          />

          {availableStatuses.length === 0 && (
            <div style={{ color: 'var(--mantine-color-gray-6)', fontSize: '0.875rem' }}>
              このステータスから変更できるステータスがありません
            </div>
          )}

          {/* メモ */}
          <Textarea
            label="メモ（任意）"
            placeholder="ステータス変更の理由などを入力してください"
            rows={4}
            maxLength={1000}
            {...form.getInputProps('notes')}
          />

          {/* アクションボタン */}
          <Group justify="flex-end" gap="md">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconX size={16} />}
              onClick={handleClose}
              disabled={isLoading}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              color="blue"
              leftSection={<IconCheck size={16} />}
              loading={isLoading}
              disabled={availableStatuses.length === 0}
            >
              更新
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
